import { useMemo } from 'react'
import useApiService from 'services/api'
import { useListProductsCatalog } from 'services/products'
import { SORT_OPTIONS, Filters } from '../constants'

interface SearchOptions {
  categoryOptions: { label: string; value: string }[]
  typeOptions: { label: string; value: string }[]
  sortOptions: { label: string; value: string }[]
  productOptions: { label: string; value: string }[]
  isOppLoading: boolean
  isProductsLoading: boolean
}

interface UseUnifiedSearchOptionsProps {
  filters: Filters
}

export function useUnifiedSearchOptions({
  filters,
}: UseUnifiedSearchOptionsProps): SearchOptions {
  const api = useApiService()

  // Get company categories and types from API.
  // Pass `false` explicitly to override the hook's default `disabled = isLimited`,
  // which would otherwise short-circuit categories for broker (limited) users and
  // force the universal-search filter to show only "All" (see FIR-6957).
  const { data: companyCatAndSub, isLoading: isOppLoading } =
    api.useGetActiveCompanyCategoriesAndTypes(false)

  // Fetch products for the product filter dropdown
  const { data: productsData, isLoading: isProductsLoading } =
    useListProductsCatalog({
      page: 1,
      page_size: 1000,
      sort: 'name',
    })

  // Create category options from API data
  const categoryOptions = useMemo(() => {
    if (!companyCatAndSub) return [{ label: 'All', value: 'all' }]

    return [
      { label: 'All', value: 'all' },
      ...companyCatAndSub.map((cat) => ({
        label: cat.name,
        value: cat.slug,
      })),
    ]
  }, [companyCatAndSub])

  // Create subcategory options from API data
  const subCatOptions = useMemo(() => {
    return (
      companyCatAndSub?.flatMap((cat) =>
        cat.sub_categories.map((subCat) => ({
          label: subCat.name,
          value: subCat.slug,
        }))
      ) ?? []
    )
  }, [companyCatAndSub])

  // Type options based on selected category
  const typeOptions = useMemo(() => {
    if (filters.category === 'all') {
      // Show all subcategories from all categories
      return [{ label: 'All', value: 'all' }, ...subCatOptions]
    } else {
      // Show subcategories for selected category
      const selectedCategory = companyCatAndSub?.find(
        (cat) => cat.slug === filters.category
      )
      if (!selectedCategory) {
        return [{ label: 'All', value: 'all' }]
      }

      return [
        { label: 'All', value: 'all' },
        ...selectedCategory.sub_categories.map((subCat) => ({
          label: subCat.name,
          value: subCat.slug,
        })),
      ]
    }
  }, [filters.category, companyCatAndSub, subCatOptions])

  // Sort options for Select component
  const sortOptions = useMemo(() => {
    return SORT_OPTIONS.map((option) => ({
      label: option.label,
      value: option.value,
    }))
  }, [])

  // Product options for variants tab filter
  const productOptions = useMemo(() => {
    if (!productsData?.results) return []
    return productsData.results.map((product) => ({
      label: product.name ?? '',
      value: product.id.toString(),
    }))
  }, [productsData])

  return {
    categoryOptions,
    typeOptions,
    sortOptions,
    productOptions,
    isOppLoading,
    isProductsLoading,
  }
}
